import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';
import ProtectedRoute from '../components/ProtectedRoute';

// Lazy load pages for better performance
const Login = lazy(() => import('../pages/auth/Login'));
const Register = lazy(() => import('../pages/auth/Register'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));

// Dashboards
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const DoctorDashboard = lazy(() => import('../pages/doctor/DoctorDashboard'));
const AssistantDashboard = lazy(() => import('../pages/assistant/AssistantDashboard'));

// Admin Pages
const DatabaseManager = lazy(() => import('../pages/admin/DatabaseManager'));

// Assistant Pages
const RoomManagement = lazy(() => import('../pages/assistant/RoomManagement'));
const DoctorCalendarsPage = lazy(() => import('../pages/assistant/DoctorCalendarsPage'));
const AssistantAppointments = lazy(() => import('../pages/assistant/AssistantAppointments'));
const Reminders = lazy(() => import('../pages/assistant/Reminders'));

// Appointments
const BookAppointment = lazy(() => import('../pages/appointments/BookAppointment'));
const AppointmentList = lazy(() => import('../pages/appointments/AppointmentList'));
const AppointmentCalendar = lazy(() => import('../pages/appointments/AppointmentCalendar'));

// User Management
const UserList = lazy(() => import('../pages/users/UserList'));
const DoctorList = lazy(() => import('../pages/users/DoctorList'));
const PatientList = lazy(() => import('../pages/users/PatientList'));

// Medical
const Prescriptions = lazy(() => import('../pages/medical/Prescriptions'));

// Communication
const Announcements = lazy(() => import('../pages/communication/Announcements'));
const EmailTemplates = lazy(() => import('../pages/communication/EmailTemplates'));
const ContactForm = lazy(() => import('../pages/communication/ContactForm'));

// Reports
const PatientReports = lazy(() => import('../pages/reports/PatientReports'));
const DoctorPerformance = lazy(() => import('../pages/reports/DoctorPerformance'));
const AppointmentAnalytics = lazy(() => import('../pages/reports/AppointmentAnalytics'));
const CustomReports = lazy(() => import('../pages/reports/CustomReports'));
const ExportData = lazy(() => import('../pages/reports/ExportData'));

// Settings
const UserSettings = lazy(() => import('../pages/settings/UserSettings'));
const NotificationSettings = lazy(() => import('../pages/settings/NotificationSettings'));
const PrivacySettings = lazy(() => import('../pages/settings/PrivacySettings'));
const SystemSettings = lazy(() => import('../pages/settings/SystemSettings'));
const ClinicSettings = lazy(() => import('../pages/settings/ClinicSettings'));

// Loading component
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
          <Route element={<MainLayout />}>
            {/* Dashboards */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/database" element={<DatabaseManager />} />
            <Route path="/doctor" element={<DoctorDashboard />} />
            <Route path="/assistant" element={<AssistantDashboard />} />

            {/* Assistant Pages */}
            <Route path="/assistant/rooms" element={<RoomManagement />} />
            <Route path="/assistant/doctor-calendars" element={<DoctorCalendarsPage />} />
            <Route path="/assistant/appointments" element={<AssistantAppointments />} />
            <Route path="/assistant/reminders" element={<Reminders />} />

            {/* Appointments */}
            <Route path="/appointments">
              <Route index element={<AppointmentList />} />
              <Route path="book" element={<BookAppointment />} />
              <Route path="calendar" element={<AppointmentCalendar />} />
            </Route>

            {/* Users */}
            <Route path="/users">
              <Route index element={<UserList />} />
              <Route path="doctors" element={<DoctorList />} />
              <Route path="patients" element={<PatientList />} />
            </Route>

            {/* Medical */}
            <Route path="/medical">
              <Route path="prescriptions" element={<Prescriptions />} />
            </Route>

            {/* Communication */}
            <Route path="/communication">
              <Route path="announcements" element={<Announcements />} />
              <Route path="email-templates" element={<EmailTemplates />} />
              <Route path="contact" element={<ContactForm />} />
            </Route>

            {/* Reports */}
            <Route path="/reports">
              <Route path="patients" element={<PatientReports />} />
              <Route path="doctor-performance" element={<DoctorPerformance />} />
              <Route path="appointments" element={<AppointmentAnalytics />} />
              <Route path="custom" element={<CustomReports />} />
              <Route path="export" element={<ExportData />} />
            </Route>

            {/* Settings */}
            <Route path="/settings">
              <Route index element={<UserSettings />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="privacy" element={<PrivacySettings />} />
              <Route path="system" element={<SystemSettings />} />
              <Route path="clinic" element={<ClinicSettings />} />
            </Route>

            {/* Default redirect based on user role */}
            <Route path="/" element={<Navigate to="/assistant" replace />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;